import type { Match } from "@/lib/types";
import {
  defaultSlotForMatch,
  slotsForMatch,
} from "@/features/matches/matchSlots";

export function recordingStatus(
  match: Match,
): "none" | "ready" | "transcoding" | "error" | "partial" {
  const slots = slotsForMatch(match);
  const states = slots.map((slot) => match.video_status?.[slot]);
  if (states.includes("error")) return "error";
  if (
    states.some((state) =>
      ["uploading", "queued", "processing", "transcoding"].includes(
        state ?? "",
      ),
    )
  )
    return "transcoding";
  const ready = (slot: string) =>
    match.video_status?.[slot] === "ready" && !!match.videos?.[slot];
  const required =
    slots[0] === "full" ? ["full"] : ["first_half", "second_half"];
  if (required.every(ready)) return "ready";
  return slots.some(ready) ? "partial" : "none";
}
export function recordingLabel(match: Match): string {
  return {
    none: "Unavailable",
    ready: "Ready",
    transcoding: "Processing",
    error: "Error",
    partial: "Partial",
  }[recordingStatus(match)];
}
export function layoutLabel(match: Match): string {
  return slotsForMatch(match)[0] === "full" ? "Full match" : "Separate recordings";
}
export function matchHref(match: Match): string {
  const slot =
    slotsForMatch(match).find(
      (s) => match.video_status?.[s] === "ready" && match.videos?.[s],
    ) ?? defaultSlotForMatch(match);
  return `/match/${encodeURIComponent(match.slug)}?slot=${encodeURIComponent(slot)}`;
}
export function matchDate(match: Match): string {
  if (!match.date) return "No date";
  const date = new Date(`${match.date}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? match.date
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
}
export function matchStatusLabel(match: Match): string {
  const status = match.match_status ?? "scheduled";
  return status.charAt(0).toUpperCase() + status.slice(1);
}
